import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import {
  Menu, X, ChevronDown, Zap, Database, Shield, Terminal,
  Users, FileText, LayoutDashboard, Smartphone, ClipboardCheck,
  MessageSquare, AlertTriangle, Clock, Layers, CheckCircle,
  ArrowRight, Building2, HardHat, Calculator, Wrench, Play } from
"lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger } from
"@/components/ui/accordion";
import { Toaster, toast } from "sonner";
import { GlowCard, GlowCardStyles } from "@/components/ui/spotlight-card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation constants (avoids inline object re-creation on each render)
const FADE_IN_UP = { opacity: 0, y: 20 };
const FADE_IN_UP_VISIBLE = { opacity: 1, y: 0 };
const TRANSITION_HALF_SEC = { duration: 0.5 };
const TRANSITION_FAST = { duration: 0.2 };
const HOVER_SCALE = { scale: 1.1 };
const TAP_SCALE = { scale: 0.95 };
const BACK_TO_TOP_INITIAL = { opacity: 0, scale: 0.8 };
const TOASTER_STYLE = {
  background: '#0F172A',
  color: '#F8FAFC',
  border: '1px solid #334155'
};

// Logo Component
const Logo = () =>
<a href="#hero" className="flex items-center gap-2">
    <img
    src="https://customer-assets.emergentagent.com/job_project-flow-beta/artifacts/dh5z86or_PM%20Flow.svg"
    alt="PM Flow Logo"
    className="h-8 w-auto" />

    <span className="badge-beta ml-2">Beta</span>
  </a>;


// Navigation Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [setScrolled]);

  const navLinks = [
  { href: "#features", label: "Product" },
  { href: "#how-it-works", label: "How it works" },
  { href: "#mobile-app", label: "Mobile app" },
  { href: "#roles", label: "For your role" },
  { href: "#pricing", label: "Pricing" },
  { href: "#faq", label: "FAQ" },
  { href: "#founder", label: "About" }];


  return (
    <header
      className={`navbar fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'py-3' : 'py-4'}`
      }
      data-testid="navbar">

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <nav className="flex items-center justify-between">
          <Logo />
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) =>
            <a
              key={link.href}
              href={link.href}
              className="nav-link text-sm font-semibold text-slate-300 hover:text-amber-400"
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}>

                {link.label}
              </a>
            )}
          </div>

          <div className="hidden lg:block">
            <a href="#beta-access">
              <Button
                className="btn-primary px-6 py-2 text-sm font-bold rounded-none"
                data-testid="nav-cta-join-beta">

                Join the beta
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-slate-300 hover:text-amber-400"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu">

            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {isOpen &&
        <div className="mobile-menu lg:hidden absolute top-full left-0 right-0 border-b border-slate-800 py-6 px-6" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) =>
            <a
              key={link.href}
              href={link.href}
              className="text-slate-300 hover:text-amber-400 font-medium py-2"
              onClick={() => setIsOpen(false)}>

                  {link.label}
                </a>
            )}
              <a href="#beta-access" onClick={() => setIsOpen(false)}>
                <Button className="inline-flex items-center justify-center whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-primary/90 h-9 btn-primary w-full text-sm font-bold gap-2 px-4 py-2 mt-4 !rounded-[10px] shadow text-primary-foreground bg-primary">
                  Join the beta
                </Button>
              </a>
            </div>
          </div>
        }
      </div>
    </header>);

};

// Hero Section with Fullscreen Video Background
const Hero = () => {
  const video1Ref = React.useRef(null);
  const video2Ref = React.useRef(null);

  React.useEffect(() => {
    const video1 = video1Ref.current;
    const video2 = video2Ref.current;
    if (!video1 || !video2) return;

    // Start video2 hidden
    video2.style.opacity = '0';
    
    const handleVideo1TimeUpdate = () => {
      // When video1 is near the end, fade in video2 and start it
      if (video1.duration - video1.currentTime < 0.5) {
        video2.currentTime = 0;
        video2.play();
        video2.style.opacity = '1';
        video1.style.opacity = '0';
      }
    };

    const handleVideo2TimeUpdate = () => {
      // When video2 is near the end, fade in video1 and start it
      if (video2.duration - video2.currentTime < 0.5) {
        video1.currentTime = 0;
        video1.play();
        video1.style.opacity = '1';
        video2.style.opacity = '0';
      }
    };

    video1.addEventListener('timeupdate', handleVideo1TimeUpdate);
    video2.addEventListener('timeupdate', handleVideo2TimeUpdate);
    
    return () => {
      video1.removeEventListener('timeupdate', handleVideo1TimeUpdate);
      video2.removeEventListener('timeupdate', handleVideo2TimeUpdate);
    };
  }, [video1Ref, video2Ref]);

  const videoSources = (
    <>
      <source
        src="https://customer-assets.emergentagent.com/job_project-flow-beta/artifacts/mdphp5k2_PM_Flow_Video_BG.mp4"
        type="video/mp4"
      />
      <source
        src="https://customer-assets.emergentagent.com/job_project-flow-beta/artifacts/dzv69cf2_PM_Flow_Hero_video_bg.webm"
        type="video/webm"
      />
    </>
  );

  return (
    <section id="hero" className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      {/* Fullscreen Video Background - Two videos for seamless loop */}
      <video
        ref={video1Ref}
        className="absolute top-0 left-0 w-full h-full object-cover z-0 transition-opacity duration-300"
        autoPlay
        muted
        playsInline
        webkit-playsinline="true"
        preload="auto"
      >
        {videoSources}
      </video>
      <video
        ref={video2Ref}
        className="absolute top-0 left-0 w-full h-full object-cover z-0 transition-opacity duration-300"
        muted
        playsInline
        webkit-playsinline="true"
        preload="auto"
      >
        {videoSources}
      </video>

      {/* Dark transparent overlay for text readability */}
      <div className="absolute inset-0 bg-black/70 z-[1]" />
      
      {/* Subtle gradient for depth */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-[#020617]/30 z-[1]" />

    {/* Content */}
    <div className="relative z-[2] min-h-screen flex items-center pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full">
        <div className="flex flex-col lg:flex-row justify-between items-end gap-12 lg:gap-16">
          {/* Left Content */}
          <div className="max-w-[800px] animate-fade-in-up">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-6">
              SaaS for electrical installation project managers and teams
            </p>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light text-white mb-8 leading-[1.1] tracking-[-1px]">
              Control every electrical
              <br />
              project from one place.
            </h1>
            
            <p className="text-lg text-slate-300 mb-12 leading-relaxed max-w-[600px]">
              PM Flow connects proposals, costing, planning, field work, and documentation into a single workspace designed for electrical installation teams.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-5 items-start">
              <a href="#beta-access">
                <Button
                className="justify-center whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-9 flex items-center hover:bg-amber-300 hover:translate-x-0.5 transition-all duration-200 text-base font-semibold gap-2.5 py-4 px-8 !rounded-[10px] shadow-[0_0_20px_rgba(251,191,36,0.4)] text-slate-950 bg-amber-400"
                data-testid="hero-cta-join-beta">

                  Get Started
                  <ArrowRight size={20} />
                </Button>
              </a>
              <a href="#how-it-works">
                <Button
                className="inline-flex items-center justify-center whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-primary/90 h-9 hover:text-amber-400 transition-colors duration-200 border-none text-base font-medium gap-2 py-4 px-8 !rounded-[10px] shadow text-slate-300 bg-transparent"
                data-testid="hero-cta-how-it-works">

                  Learn more
                </Button>
              </a>
            </div>
          </div>

          {/* Stats Section - Right Side */}
          <div className="flex gap-12 lg:gap-20 items-end animate-fade-in-up stagger-2">
            <div className="text-center">
              <div className="text-5xl lg:text-6xl font-light text-white leading-none mb-3">100%</div>
              <div className="text-base text-slate-400 font-normal">Project visibility</div>
            </div>
            <div className="text-center">
              <div className="text-5xl lg:text-6xl font-light text-white leading-none mb-3">1</div>
              <div className="text-base text-slate-400 font-normal">Unified platform</div>
            </div>
          </div>
        </div>

        {/* Benefits row at bottom */}
        <div className="mt-16 lg:mt-24 flex flex-wrap gap-6 animate-fade-in-up stagger-3">
          {[
        "End-to-end project visibility",
        "Built for electrical standards",
        "Real-time field updates"].
        map((benefit) =>
        <div key={benefit} className="flex items-center gap-2 text-sm text-slate-300">
              <CheckCircle size={16} className="text-amber-400" />
              <span>{benefit}</span>
            </div>
        )}
        </div>
      </div>
    </div>

    {/* Scroll indicator */}
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[2] animate-bounce">
      <ChevronDown size={24} className="text-slate-400" />
    </div>
  </section>
  );
};


// Trust Strip
const TrustStrip = () =>
<section className="py-12 border-y border-slate-800/50" data-testid="trust-strip">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
      <p className="text-center text-slate-500 text-sm mb-6">
        Built with the realities of electrical installation projects in mind
      </p>
      <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
        {["Electrical contractors", "Industrial installations", "Commercial projects", "Residential teams"].map((item) =>
      <div key={item} className="trust-item flex items-center gap-2 text-slate-400">
            <Zap size={16} className="text-amber-400" />
            <span className="text-sm font-medium">{item}</span>
          </div>
      )}
      </div>
    </div>
  </section>;


// Pain Points Section
const PainPoints = () => {
  const painPoints = [
  {
    icon: <AlertTriangle className="text-amber-500" size={28} />,
    title: "Scattered information",
    description: "Proposals in email, schedules in Excel, photos on phones. Critical project data lives in a dozen different places."
  },
  {
    icon: <Clock className="text-amber-500" size={28} />,
    title: "No real-time view from the field",
    description: "Delays, missed updates, unclear accountability. You find out about problems days after they happen."
  },
  {
    icon: <Shield className="text-amber-500" size={28} />,
    title: "Standards and compliance risk",
    description: "Unclear checklists, missing documentation. Audits become stressful, and mistakes slip through."
  },
  {
    icon: <Database className="text-amber-500" size={28} />,
    title: "Inventory and cost surprises",
    description: "Materials untracked, overspending revealed too late. Your margins erode without warning."
  }];


  return (
    <section id="pain-points" className="py-24 sm:py-32 relative" data-testid="pain-points-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center mb-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-50 mb-4">
            If you manage electrical projects like this, PM Flow is for you.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {painPoints.map((point, idx) =>
          <GlowCard
            key={point.title}
            glowColor="amber"
            className="p-8"
            data-testid={`pain-card-${idx}`}>
              <div className="mb-4">{point.icon}</div>
              <h3 className="text-xl font-semibold text-slate-100 mb-3">{point.title}</h3>
              <p className="text-slate-400">{point.description}</p>
          </GlowCard>
          )}
        </div>
      </div>
    </section>);

};

// Features Section
const Features = () => {
  const features = [
  {
    icon: <FileText className="text-amber-400" size={28} />,
    title: "Proposals & Costing",
    description: "Create structured client proposals with clear scope, materials, and labor. Auto-calculate project costs and margins so you quote with confidence.",
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80"
  },
  {
    icon: <LayoutDashboard className="text-amber-400" size={28} />,
    title: "Project Planning & Tasks",
    description: "Turn accepted proposals into actionable project plans. Create tasks, assign responsibilities, and set milestones for each phase.",
    image: "https://images.unsplash.com/photo-1507925921958-8a62f3d1a50d?w=800&q=80"
  },
  {
    icon: <Layers className="text-amber-400" size={28} />,
    title: "Inventory & Materials",
    description: "Track materials per project and from central inventory. See what's reserved, used, and remaining, avoiding last-minute surprises.",
    image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80"
  },
  {
    icon: <Shield className="text-amber-400" size={28} />,
    title: "Standards, Safety & Compliance",
    description: "Attach standards and checklists to projects and tasks. Document inspections, tests, and approvals so audits are easier.",
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80"
  },
  {
    icon: <ClipboardCheck className="text-amber-400" size={28} />,
    title: "Field Documentation & Deviations",
    description: "Teams capture photos, notes, and deviation reports from the mobile app. Keep a complete history of what happened, when, and who was involved.",
    image: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&q=80"
  },
  {
    icon: <MessageSquare className="text-amber-400" size={28} />,
    title: "Team Communication",
    description: "Keep discussion tied to specific tasks or projects. Replace fragmented chats with context-rich updates.",
    image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
  }];


  return (
    <section id="features" className="py-24 sm:py-32 bg-slate-900/30" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-4">
            Core Capabilities
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-50">
            Everything your electrical projects need, in one flow.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, idx) =>
          <GlowCard
            key={feature.title}
            glowColor="amber"
            className="overflow-hidden ambient-glow"
            data-testid={`feature-card-${idx}`}>
              {/* Image Section */}
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={feature.image}
                  alt={feature.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/40 to-transparent opacity-70" />
              </div>
              {/* Content */}
              <div className="p-6">
                <div className="mb-4 p-3 bg-slate-800/50 w-fit rounded-lg">
                  {feature.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-slate-100 mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
          </GlowCard>
          )}
        </div>

        {/* Video Showcase */}
        <div className="mt-16 relative">
          <motion.div
            initial={FADE_IN_UP}
            animate={FADE_IN_UP_VISIBLE}
            transition={TRANSITION_HALF_SEC}
            className="flex flex-col items-center gap-8"
          >
            {/* Title */}
            <div className="text-center">
              <h3 className="text-2xl font-bold text-slate-50 mb-2">
                See PM Flow in action
              </h3>
              <p className="text-slate-400">Learn about PM Flow in less than 2 minutes!</p>
            </div>

            {/* Skill Badges */}
            <div className="flex flex-wrap items-center justify-center gap-3">
              {[
                { icon: <FileText className="h-4 w-4 text-amber-400" />, label: "Proposals" },
                { icon: <LayoutDashboard className="h-4 w-4 text-amber-400" />, label: "Planning" },
                { icon: <Layers className="h-4 w-4 text-amber-400" />, label: "Inventory" },
                { icon: <Shield className="h-4 w-4 text-amber-400" />, label: "Compliance" },
                { icon: <ClipboardCheck className="h-4 w-4 text-amber-400" />, label: "Documentation" },
                { icon: <MessageSquare className="h-4 w-4 text-amber-400" />, label: "Communication" },
              ].map((skill) => (
                <Badge 
                  key={skill.label} 
                  variant="outline" 
                  className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 border-slate-700 text-slate-300 hover:border-amber-400/50 transition-colors cursor-default"
                >
                  {skill.icon}
                  <span className="font-medium">{skill.label}</span>
                </Badge>
              ))}
            </div>

            {/* Video Card with GlowCard */}
            <VideoShowcase 
              videoId="olcoAjpU5Ls"
              title="Welcome to PM Flow"
              description="Discover how PM Flow streamlines your electrical projects"
            />
          </motion.div>
        </div>
      </div>
    </section>);

};

// Video Showcase Component
const VideoShowcase = ({ videoId, title, description }) => {
  const [isPlaying, setIsPlaying] = React.useState(false);

  return (
    <GlowCard glowColor="amber" className="w-full max-w-4xl overflow-hidden ambient-glow">
      <div 
        className="group relative cursor-pointer"
        onClick={() => setIsPlaying(true)}
      >
        {!isPlaying ? (
          <>
            {/* Thumbnail */}
            <div className="aspect-video w-full overflow-hidden">
              <img
                src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                alt={title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                onError={(e) => {
                  e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                }}
              />
            </div>
            
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/40 to-transparent" />
            
            {/* Play Button */}
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                whileHover={HOVER_SCALE}
                whileTap={TAP_SCALE}
                className="w-20 h-20 rounded-full bg-amber-400 flex items-center justify-center shadow-lg shadow-amber-400/30"
              >
                <Play className="w-8 h-8 text-slate-950 ml-1" fill="currentColor" />
              </motion.div>
            </div>
            
            {/* Title & Description */}
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h4 className="text-xl font-bold text-white mb-1">{title}</h4>
              <p className="text-sm text-slate-300">{description}</p>
            </div>
          </>
        ) : (
          /* YouTube Embed */
          <div className="aspect-video w-full">
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
              title={title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}
      </div>
    </GlowCard>
  );
};

// How It Works Section
const HowItWorks = () => {
  const steps = [
  {
    number: "1",
    title: "Capture & Qualify",
    description: "Log a new client request, capture requirements, and estimate scope."
  },
  {
    number: "2",
    title: "Propose & Approve",
    description: "Create a proposal with pricing. Once approved, convert it into a live project."
  },
  {
    number: "3",
    title: "Plan & Assign",
    description: "Define tasks, assign them to electricians and teams, and schedule phases."
  },
  {
    number: "4",
    title: "Execute & Document",
    description: "Field teams use the mobile app to log work, photos, deviations, and progress."
  },
  {
    number: "5",
    title: "Deliver & Improve",
    description: "Generate final documentation and reports. Review lessons learned for future projects."
  }];


  return (
    <section id="how-it-works" className="py-24 sm:py-32" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-4">
            The Process
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-50">
            From first contact to final delivery, step by step.
          </h2>
        </div>

        <div className="max-w-3xl mx-auto">
          {steps.map((step, idx) =>
          <div
            key={step.number}
            className="timeline-step flex gap-6 pb-12"
            data-testid={`timeline-step-${idx}`}>

              <div className="step-number">{step.number}</div>
              <div className="timeline-connector" />
              <div className="pt-2">
                <h3 className="text-xl font-semibold text-slate-100 mb-2">{step.title}</h3>
                <p className="text-slate-400">{step.description}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

// Import Tabs for Mobile App Section
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

// Mobile App Section with Carousel
const MobileApp = () => {
  const mobileScreens = [
    {
      value: "tasks",
      label: "Tasks",
      image: "https://customer-assets.emergentagent.com/job_project-flow-beta/artifacts/slqo2z4d_Tasks.png",
      alt: "PM Flow Tasks screen"
    },
    {
      value: "deviations",
      label: "Deviations",
      image: "https://customer-assets.emergentagent.com/job_project-flow-beta/artifacts/zgnf4gug_Deviation.png",
      alt: "PM Flow Deviation report screen"
    },
    {
      value: "chat",
      label: "Chat",
      image: "https://customer-assets.emergentagent.com/job_project-flow-beta/artifacts/66igmqub_Chat.png",
      alt: "PM Flow Chat screen"
    }
  ];

  const steps = [
    {
      id: "step-1",
      title: "View assigned tasks",
      description: "Technicians see their daily tasks, priorities, and deadlines at a glance. Mark tasks complete with one tap."
    },
    {
      id: "step-2",
      title: "Report deviations instantly",
      description: "Capture photos, add notes, and tag location when something doesn't go as planned. Keep everyone informed in real-time."
    },
    {
      id: "step-3",
      title: "Stay connected with your team",
      description: "Message project managers and colleagues directly within the app. All communication stays tied to the relevant project."
    }
  ];

  return (
    <section id="mobile-app" className="py-24 sm:py-32 relative overflow-hidden" data-testid="mobile-app-section">
      {/* Background */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-slate-900/50 to-slate-950" />
      
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left Column - Content */}
          <div className="lg:col-span-6">
            <Badge className="mb-6 bg-amber-400/10 text-amber-400 border-amber-400/30 hover:bg-amber-400/20">
              Field Ready
            </Badge>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-50 leading-tight mb-6">
              Designed for teams in the field.
            </h2>

            <p className="text-slate-400 mb-6 max-w-xl">
              The PM Flow mobile app keeps your entire electrical team connected, whether they're in the office or on-site.
            </p>

            {/* Stats chips */}
            <div className="flex flex-wrap gap-2 mb-10">
              {["Real-time sync", "Offline mode", "iOS & Android"].map((stat) => (
                <Badge key={stat} variant="secondary" className="bg-slate-800 text-slate-300 border-slate-700">
                  {stat}
                </Badge>
              ))}
            </div>

            {/* Steps Accordion */}
            <Accordion type="single" collapsible className="w-full max-w-xl">
              {steps.map((step) => (
                <AccordionItem key={step.id} value={step.id} className="border-slate-700">
                  <AccordionTrigger className="text-left text-base font-medium text-slate-100 hover:text-amber-400 hover:no-underline">
                    {step.title}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-slate-400">
                    {step.description}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {/* CTAs */}
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#beta-access">
                <Button className="btn-primary px-6 py-3 font-bold !rounded-[10px]">
                  Get early access
                </Button>
              </a>
              <a href="#features">
                <Button className="btn-secondary px-6 py-3 font-semibold !rounded-[10px]">
                  View all features
                </Button>
              </a>
            </div>
          </div>

          {/* Right Column - Phone Carousel */}
          <div className="lg:col-span-6">
            <Tabs defaultValue="tasks" className="w-full">
              {/* Phone Frame */}
              <div className="relative flex items-center justify-center" style={{ height: 620 }}>
                {mobileScreens.map((screen, idx) => (
                  <TabsContent
                    key={screen.value}
                    value={screen.value}
                    className="absolute inset-0 m-0 flex items-center justify-center data-[state=inactive]:hidden"
                  >
                    {/* Phone Frame */}
                    <div className="relative w-72 h-[580px] bg-slate-950 rounded-[3rem] border-[3px] border-slate-700 shadow-2xl overflow-hidden ambient-glow">
                      {/* Dynamic Island / Notch */}
                      <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-7 bg-slate-950 rounded-full z-20 border border-slate-800" />
                      {/* Screen with scrollable content */}
                      <div className="absolute inset-[3px] rounded-[2.7rem] overflow-hidden bg-slate-950">
                        <div className="h-full w-full overflow-y-auto scrollbar-hide">
                          <img
                            src={screen.image}
                            alt={screen.alt}
                            className="w-full h-auto"
                            loading={idx === 0 ? "eager" : "lazy"}
                          />
                        </div>
                      </div>
                      {/* Home indicator */}
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-slate-600 rounded-full z-20" />
                    </div>
                  </TabsContent>
                ))}
              </div>

              {/* Tab Controls - separate from phone */}
              <div className="flex justify-center mt-6">
                <TabsList className="flex gap-2 rounded-xl border border-slate-700 bg-slate-900/90 p-1 backdrop-blur">
                  {mobileScreens.map((screen) => (
                    <TabsTrigger
                      key={screen.value}
                      value={screen.value}
                      className="rounded-lg px-5 py-2 text-sm font-medium text-slate-400 data-[state=active]:bg-amber-400 data-[state=active]:text-slate-950"
                    >
                      {screen.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
            </Tabs>
          </div>
        </div>
      </div>
    </section>
  );
};


// Roles Section
const RolesSection = () => {
  const roles = [
  {
    icon: <LayoutDashboard className="text-amber-400" size={24} />,
    title: "For Project Managers",
    description: "Overview of all projects, progress, risks, and deadlines. Faster decisions with complete information in one place."
  },
  {
    icon: <HardHat className="text-amber-400" size={24} />,
    title: "For Work Managers",
    description: "Clear daily plans, assigned tasks, and status updates from the field. Fewer surprises and easier coordination between office and site."
  },
  {
    icon: <Building2 className="text-amber-400" size={24} />,
    title: "For Electrical Consultants",
    description: "See that technical intent and standards are followed through execution. Traceable documentation for decisions, deviations, and sign-offs."
  },
  {
    icon: <Calculator className="text-amber-400" size={24} />,
    title: "For Installation Calculators",
    description: "Link calculations and offers directly to live projects. Compare estimated vs. actual materials and time to improve future quotes."
  },
  {
    icon: <Wrench className="text-amber-400" size={24} />,
    title: "For Electrical Teams",
    description: "Simple, clear tasks and checklists on mobile. Easy way to report deviations and share photos, without extra admin work."
  }];


  return (
    <section id="roles" className="py-24 sm:py-32 bg-slate-900/30" data-testid="roles-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-4">
            Built For You
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-50">
            Built for every role in your electrical project team.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {roles.map((role, idx) =>
          <GlowCard
            key={role.title}
            glowColor="amber"
            className="p-8"
            data-testid={`role-card-${idx}`}>
              <div className="mb-4">{role.icon}</div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3">{role.title}</h3>
              <p className="text-slate-400 text-sm">{role.description}</p>
          </GlowCard>
          )}
        </div>
      </div>
    </section>);

};

// Beta Access & Pricing Section
const BetaAccess = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    company: '',
    role: '',
    country: '',
    team_size: '',
    current_tools: '',
    newsletter: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.full_name || !formData.email || !formData.role) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/beta-signups`, formData);
      toast.success("You're on the list! We'll be in touch soon.");
      setFormData({
        full_name: '',
        email: '',
        company: '',
        role: '',
        country: '',
        team_size: '',
        current_tools: '',
        newsletter: false
      });
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || "This email is already registered.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="beta-access" className="py-24 sm:py-32" data-testid="beta-access-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left - Content */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-4">
              Early Access
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-50 mb-6">
              Join the beta and help shape PM Flow.
            </h2>
            
            <p className="text-slate-400 mb-6">
              PM Flow is currently in active development. As a beta user, you'll get:
            </p>
            
            <div className="space-y-4 mb-8">
              {[
              "Direct influence on the product roadmap",
              "Early access and onboarding support",
              "Opportunity to provide feedback that shapes real-world features",
              "Special launch pricing when we go live"].
              map((item) =>
              <div key={item} className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-lime-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300">{item}</span>
                </div>
              )}
            </div>

            {/* Pricing Preview */}
            <GlowCard id="pricing" glowColor="amber" className="p-6" data-testid="pricing-preview">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Pricing Preview</h3>
              <p className="text-slate-400 text-sm mb-4">
                PM Flow will be priced affordably for small to mid-sized electrical teams. Final pricing is not set yet, but expect tiers like:
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-slate-300">
                  <span>Small teams (up to 5 users)</span>
                  <span className="text-amber-400">Coming soon</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Larger teams and enterprises</span>
                  <span className="text-amber-400">Custom pricing</span>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-4">
                Beta users will receive special launch pricing.
              </p>
            </GlowCard>
          </div>

          {/* Right - Form */}
          <GlowCard glowColor="amber" className="p-8" data-testid="beta-signup-form">
            <h3 className="text-xl font-semibold text-slate-100 mb-6">Request Beta Access</h3>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  Full Name <span className="text-red-400">*</span>
                </label>
                <Input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="dark-input rounded-none"
                  data-testid="input-full-name"
                  required />

              </div>

              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  Work Email <span className="text-red-400">*</span>
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  className="dark-input rounded-none"
                  data-testid="input-email"
                  required />

              </div>

              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  Company Name
                </label>
                <Input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Your company"
                  className="dark-input rounded-none"
                  data-testid="input-company" />

              </div>

              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  Role <span className="text-red-400">*</span>
                </label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => handleSelectChange('role', value)}>

                  <SelectTrigger
                    className="dark-input rounded-none bg-slate-900 border-slate-700 text-slate-100"
                    data-testid="select-role">

                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="project-manager" className="text-slate-100 focus:bg-slate-800">Project Manager</SelectItem>
                    <SelectItem value="work-manager" className="text-slate-100 focus:bg-slate-800">Work Manager</SelectItem>
                    <SelectItem value="electrical-consultant" className="text-slate-100 focus:bg-slate-800">Electrical Consultant with project responsibility</SelectItem>
                    <SelectItem value="installation-calculator" className="text-slate-100 focus:bg-slate-800">Electrical Installation Calculator</SelectItem>
                    <SelectItem value="electrical-professional" className="text-slate-100 focus:bg-slate-800">Electrical professional (engineer/technician)</SelectItem>
                    <SelectItem value="other" className="text-slate-100 focus:bg-slate-800">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">
                    Country
                  </label>
                  <Input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder="Your country"
                    className="dark-input rounded-none"
                    data-testid="input-country" />

                </div>

                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">
                    Team Size
                  </label>
                  <Select
                    value={formData.team_size}
                    onValueChange={(value) => handleSelectChange('team_size', value)}>

                    <SelectTrigger
                      className="dark-input rounded-none bg-slate-900 border-slate-700 text-slate-100"
                      data-testid="select-team-size">

                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="1-5" className="text-slate-100 focus:bg-slate-800">1-5</SelectItem>
                      <SelectItem value="6-20" className="text-slate-100 focus:bg-slate-800">6-20</SelectItem>
                      <SelectItem value="21-50" className="text-slate-100 focus:bg-slate-800">21-50</SelectItem>
                      <SelectItem value="50+" className="text-slate-100 focus:bg-slate-800">50+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  How do you currently manage your projects?
                </label>
                <Textarea
                  name="current_tools"
                  value={formData.current_tools}
                  onChange={handleChange}
                  placeholder="Spreadsheets, WhatsApp, other tools..."
                  className="dark-input rounded-none min-h-[80px]"
                  data-testid="input-current-tools" />

              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="newsletter"
                  name="newsletter"
                  checked={formData.newsletter}
                  onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, newsletter: checked }))}
                  className="mt-0.5 border-slate-600 data-[state=checked]:bg-amber-400 data-[state=checked]:border-amber-400"
                  data-testid="checkbox-newsletter" />

                <label htmlFor="newsletter" className="text-sm text-slate-400 cursor-pointer">
                  I'd like to receive updates about the PM Flow beta and launch.
                </label>
              </div>

              <Button
                type="submit"
                className="inline-flex items-center justify-center whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-primary/90 h-9 btn-primary w-full text-base font-bold gap-2 px-4 py-3 !rounded-[10px] shadow text-primary-foreground bg-primary"
                disabled={isSubmitting}
                data-testid="submit-beta-form">

                {isSubmitting ? 'Submitting...' : 'Request beta access'}
              </Button>

              <p className="text-xs text-slate-500 text-center">
                We respect your inbox. No spam—only important updates about PM Flow.
              </p>
            </form>
          </GlowCard>
        </div>
      </div>
    </section>);

};

// Founder Section with Spotlight Glow Card
const Founder = () => {
  return (
    <section id="founder" className="py-24 sm:py-32 bg-slate-900/30" data-testid="founder-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-4">
            The Creator
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-50">
            Built by an electrical engineer who lives this work.
          </h2>
        </div>

        <div className="flex justify-center">
          {/* Spotlight Glow Profile Card */}
          <GlowCard glowColor="amber" className="w-80 sm:w-96">
            <div className="p-8 flex flex-col items-center justify-center text-center">
              {/* Profile Image */}
              <img 
                src="https://customer-assets.emergentagent.com/job_project-flow-beta/artifacts/vltkgxul_Karim%20Tijani.png"
                alt="Karim Tijani - Founder of PM Flow"
                className="w-32 h-32 rounded-full shadow-md mb-6 object-cover border-2 border-amber-400/30"
              />
              
              {/* Name & Title */}
              <h3 className="text-2xl font-bold text-white mb-1">Karim Tijani</h3>
              <p className="text-sm text-amber-400 font-medium mb-4">Founder & Creator of PM Flow</p>
              
              {/* Description */}
              <p className="text-sm text-slate-400 mb-6 px-4 leading-relaxed">
                High-voltage electrical engineer and electrical installation manager with over 15 years of experience in the electrical field. Combining a decade of UX/UI design expertise with real field knowledge to build tools that fit how electrical teams actually work.
              </p>

              {/* Stats/Skills */}
              <div className="flex flex-wrap justify-center gap-2 mb-6">
                {["15+ Years Electrical", "10+ Years UX/UI Design"].map((skill) => (
                  <span key={skill} className="text-xs bg-slate-800 text-slate-300 px-3 py-1 rounded-full">
                    {skill}
                  </span>
                ))}
              </div>
              
              {/* Social Links */}
              <div className="flex space-x-4 text-slate-400">
                <a 
                  href="https://www.linkedin.com/in/karim-tijani" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:-translate-y-0.5 hover:text-amber-400 transition"
                  data-testid="founder-linkedin"
                >
                  <svg className="size-7" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M12.51 8.796v1.697a3.738 3.738 0 0 1 3.288-1.684c3.455 0 4.202 2.16 4.202 4.97V19.5h-3.2v-5.072c0-1.21-.244-2.766-2.128-2.766-1.827 0-2.139 1.317-2.139 2.676V19.5h-3.19V8.796h3.168ZM7.2 6.106a1.61 1.61 0 0 1-.988 1.483 1.595 1.595 0 0 1-1.743-.348A1.607 1.607 0 0 1 5.6 4.5a1.601 1.601 0 0 1 1.6 1.606Z" clipRule="evenodd" />
                    <path d="M7.2 8.809H4V19.5h3.2V8.809Z" />
                  </svg>
                </a>
                <a 
                  href="https://x.com/AssistaStudio" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:-translate-y-0.5 hover:text-amber-400 transition"
                  data-testid="founder-twitter"
                >
                  <svg className="size-7" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M13.795 10.533 20.68 2h-3.073l-5.255 6.517L7.69 2H1l7.806 10.91L1.47 22h3.074l5.705-7.07L15.31 22H22l-8.205-11.467Zm-2.38 2.95L9.97 11.464 4.36 3.627h2.31l4.528 6.317 1.443 2.02 6.018 8.409h-2.31l-4.934-6.89Z"/>
                  </svg>
                </a>
              </div>
            </div>
          </GlowCard>
        </div>
      </div>
    </section>
  );
};


// FAQ Section with Tabs
import { FAQ as FAQTabs } from "@/components/ui/faq-tabs";

const FAQSection = () => {
  const categories = {
    "product": "Product & Features",
    "beta": "Beta Program",
    "pricing": "Pricing & Plans"
  };

  const faqData = {
    "product": [
    {
      question: "Who is PM Flow for?",
      answer: "PM Flow is built specifically for electrical installation teams: Project Managers, Work Managers, Electrical Consultants with project responsibility, Electrical Installation Calculators, and electrical professionals (engineers, technicians, foremen) involved in planning, executing, and documenting installation projects."
    },
    {
      question: "Is PM Flow only for high-voltage projects?",
      answer: "No. While PM Flow is designed to handle the complexity of large-scale and high-voltage installations, it works equally well for commercial, industrial, and residential electrical projects of any size."
    },
    {
      question: "What platforms will PM Flow support?",
      answer: "PM Flow will be available as a web application (accessible from any modern browser) and as a mobile app for iOS and Android, designed specifically for field use."
    }],

    "beta": [
    {
      question: "When will the beta start?",
      answer: "We're currently onboarding beta users in phases. Sign up for early access, and we'll notify you as soon as a spot opens up. Early signups get priority access."
    },
    {
      question: "How many people in my team can join the beta?",
      answer: "During beta, we'll work with you to determine the right number of users for effective testing. Most beta teams range from 3-15 users across different roles."
    },
    {
      question: "What kind of support do beta users receive?",
      answer: "Beta users receive direct access to our team for onboarding support, priority feature requests, and dedicated channels for feedback and bug reporting. We're committed to making your beta experience smooth and valuable."
    }],

    "pricing": [
    {
      question: "Will there be a free trial after launch?",
      answer: "Yes. We plan to offer a free trial period so teams can experience the full value of PM Flow before committing. Beta users will receive extended trial periods and special pricing."
    },
    {
      question: "What pricing tiers will be available?",
      answer: "PM Flow will be priced affordably for small to mid-sized electrical teams. We're planning tiers for small teams (up to 5 users) and larger teams/enterprises with custom pricing. Beta users will receive special launch pricing."
    },
    {
      question: "Are there any hidden costs or fees?",
      answer: "No hidden costs. Our pricing will be transparent and straightforward, covering all core features. Additional premium features or integrations may be available as add-ons, but the base platform will include everything you need to manage your electrical projects."
    }]

  };

  return (
    <section id="faq" className="py-24 sm:py-32" data-testid="faq-section">
      <FAQTabs
        title="Frequently Asked Questions"
        subtitle="Got Questions? We've Got Answers"
        categories={categories}
        faqData={faqData}
        className="relative overflow-hidden !pt-[46px] !pb-[48px] !pl-[16px] !pr-[16px] !rounded-[-22px] text-foreground !bg-[rgba(3,4,7,0.01)]" />

      
      {/* CTA below FAQ */}
      <div className="text-center mt-16">
        <p className="text-slate-400 mb-6">Still have questions?</p>
        <a href="#beta-access">
          <Button
            className="btn-primary px-8 py-3 font-bold !rounded-[10px]"
            data-testid="faq-cta-beta">

            Join the beta and let's talk
          </Button>
        </a>
      </div>
    </section>);

};

// Import Footer components
import { TextHoverEffect, FooterBackgroundGradient } from "@/components/ui/hover-footer";
import { Mail, MapPin, Linkedin } from "lucide-react";

// Footer
const Footer = () => {
  const footerLinks = [
    {
      title: "Product",
      links: [
        { label: "Features", href: "#features" },
        { label: "How it works", href: "#how-it-works" },
        { label: "Mobile App", href: "#mobile-app" },
        { label: "For your role", href: "#roles" },
      ],
    },
    {
      title: "Company",
      links: [
        { label: "About", href: "#founder" },
        { label: "Pricing", href: "#pricing" },
        { label: "FAQ", href: "#faq" },
        { label: "Beta Access", href: "#beta-access", highlight: true },
      ],
    },
  ];

  const contactInfo = [
    {
      icon: <Mail size={18} className="text-amber-400" />,
      text: "assistastudio@gmail.com",
      href: "mailto:assistastudio@gmail.com",
    },
    {
      icon: <MapPin size={18} className="text-amber-400" />,
      text: "Stockholm, Sweden",
    },
  ];

  const socialLinks = [
    { 
      icon: (
        <svg className="size-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
          <path fillRule="evenodd" d="M12.51 8.796v1.697a3.738 3.738 0 0 1 3.288-1.684c3.455 0 4.202 2.16 4.202 4.97V19.5h-3.2v-5.072c0-1.21-.244-2.766-2.128-2.766-1.827 0-2.139 1.317-2.139 2.676V19.5h-3.19V8.796h3.168ZM7.2 6.106a1.61 1.61 0 0 1-.988 1.483 1.595 1.595 0 0 1-1.743-.348A1.607 1.607 0 0 1 5.6 4.5a1.601 1.601 0 0 1 1.6 1.606Z" clipRule="evenodd" />
          <path d="M7.2 8.809H4V19.5h3.2V8.809Z" />
        </svg>
      ), 
      label: "LinkedIn", 
      href: "https://www.linkedin.com/in/karim-tijani" 
    },
    { 
      icon: (
        <svg className="size-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
          <path d="M13.795 10.533 20.68 2h-3.073l-5.255 6.517L7.69 2H1l7.806 10.91L1.47 22h3.074l5.705-7.07L15.31 22H22l-8.205-11.467Zm-2.38 2.95L9.97 11.464 4.36 3.627h2.31l4.528 6.317 1.443 2.02 6.018 8.409h-2.31l-4.934-6.89Z"/>
        </svg>
      ), 
      label: "X", 
      href: "https://x.com/AssistaStudio" 
    },
  ];

  return (
    <footer className="bg-slate-950/50 relative rounded-3xl overflow-hidden mx-4 sm:mx-8 mb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto p-8 sm:p-14 z-40 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-8 lg:gap-16 pb-12">
          {/* Brand section */}
          <div className="flex flex-col space-y-4">
            <div className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_project-flow-beta/artifacts/dh5z86or_PM%20Flow.svg" 
                alt="PM Flow Logo" 
                className="h-10 w-auto"
              />
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">
              PM Flow is a SaaS platform for electrical installation project managers and teams. From first client call to final inspection.
            </p>
          </div>

          {/* Footer link sections */}
          {footerLinks.map((section) => (
            <div key={section.title}>
              <h4 className="text-white text-lg font-semibold mb-6">
                {section.title}
              </h4>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.label} className="relative">
                    <a
                      href={link.href}
                      className="text-slate-400 hover:text-amber-400 transition-colors"
                    >
                      {link.label}
                    </a>
                    {link.highlight && (
                      <span className="absolute top-0 right-[-10px] w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact section */}
          <div>
            <h4 className="text-white text-lg font-semibold mb-6">
              Contact Us
            </h4>
            <ul className="space-y-4">
              {contactInfo.map((item) => (
                <li key={item.text} className="flex items-center space-x-3">
                  {item.icon}
                  {item.href ? (
                    <a
                      href={item.href}
                      className="text-slate-400 hover:text-amber-400 transition-colors text-sm"
                      data-testid="footer-email"
                    >
                      {item.text}
                    </a>
                  ) : (
                    <span className="text-slate-400 text-sm">
                      {item.text}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <hr className="border-t border-slate-800 my-8" />

        {/* Footer bottom */}
        <div className="flex flex-col md:flex-row justify-between items-center text-sm space-y-4 md:space-y-0 relative z-30">
          {/* Social icons */}
          <div className="flex space-x-6 text-slate-400">
            {socialLinks.map(({ icon, label, href }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="hover:text-amber-400 transition-colors"
              >
                {icon}
              </a>
            ))}
          </div>

          {/* Copyright */}
          <p className="text-slate-500 text-center md:text-left">
            © {new Date().getFullYear()} PM Flow. All rights reserved. Created by Karim Tijani.
          </p>
        </div>
      </div>

      {/* Text hover effect */}
      <div className="lg:flex hidden h-[16rem] mt-[5px]">
        <TextHoverEffect text="PM FLOW" className="z-10 pointer-events-auto" />
      </div>

      <FooterBackgroundGradient />
    </footer>
  );
};


// Back to Top Button
const BackToTopButton = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [setVisible]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <motion.button
      data-testid="back-to-top-btn"
      onClick={scrollToTop}
      initial={BACK_TO_TOP_INITIAL}
      animate={{ opacity: visible ? 1 : 0, scale: visible ? 1 : 0.8 }}
      transition={TRANSITION_FAST}
      className={`fixed bottom-8 right-8 z-50 w-12 h-12 !rounded-[10px] bg-amber-400 hover:bg-amber-300 text-slate-900 flex items-center justify-center shadow-lg shadow-amber-400/20 transition-colors ${!visible ? 'pointer-events-none' : ''}`}
      aria-label="Back to top"
    >
      <ChevronDown className="w-5 h-5 rotate-180" />
    </motion.button>
  );
};

// Main App Component
import { Routes, Route } from "react-router-dom";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

function LandingPage() {
  return (
    <div className="min-h-screen bg-[#020617]">
      <GlowCardStyles />
      <Toaster
        position="top-right"
        toastOptions={{
          style: TOASTER_STYLE
        }} />

      <Navbar />
      <main>
        <Hero />
        <TrustStrip />
        <PainPoints />
        <Features />
        <HowItWorks />
        <MobileApp />
        <RolesSection />
        <BetaAccess />
        <Founder />
        <FAQSection />
      </main>
      <Footer />
      <BackToTopButton />
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/admin/forgot-password" element={<ForgotPassword />} />
      <Route path="/admin/reset-password" element={<ResetPassword />} />
    </Routes>
  );
}

export default App;